/**
 * Typed binding for the /admin/facilities/* endpoints (PR #5b).
 *
 * Facilities are immutable once created. The client exposes:
 *   - list(query)   → left-pane of the Facilities master/detail view.
 *   - get(id)       → fetch a single facility when an admin clicks a row.
 *   - create(body)  → "+ New Facility" form submission.
 *   - activate(id)  → "Activate" button in the detail pane.
 *
 * `list` and `get` require `facility.read.all` OR `facility.write`.
 * `create` and `activate` require `facility.write`.
 */

import {
  FacilityListResponseSchema,
  FacilityResponseSchema,
  type FacilityCreateRequest,
  type FacilityListQuery,
  type FacilityListResponse,
  type FacilityResponse,
} from '../schemas/facility.js';
import type { RequestOptions, Transport } from './transport.js';

export interface FacilitiesAdminClient {
  list(query?: FacilityListQuery, options?: RequestOptions): Promise<FacilityListResponse>;
  get(id: string, options?: RequestOptions): Promise<FacilityResponse>;
  create(body: FacilityCreateRequest, options?: RequestOptions): Promise<FacilityResponse>;
  activate(id: string, options?: RequestOptions): Promise<FacilityResponse>;
}

const buildListPath = (query: FacilityListQuery | undefined): string => {
  if (!query) return '/admin/facilities';
  const params = new URLSearchParams();
  if (query.entityId !== undefined) params.set('entityId', query.entityId);
  if (query.isActive !== undefined) params.set('isActive', String(query.isActive));
  const qs = params.toString();
  return qs.length === 0 ? '/admin/facilities' : `/admin/facilities?${qs}`;
};

export const createFacilitiesAdminClient = (transport: Transport): FacilitiesAdminClient => ({
  list: (query, options) =>
    transport.get(buildListPath(query), FacilityListResponseSchema, options),
  get: (id, options) =>
    transport.get(`/admin/facilities/${encodeURIComponent(id)}`, FacilityResponseSchema, options),
  create: (body, options) =>
    transport.post('/admin/facilities', FacilityResponseSchema, body, options),
  activate: (id, options) =>
    transport.post(
      `/admin/facilities/${encodeURIComponent(id)}/activate`,
      FacilityResponseSchema,
      {},
      options,
    ),
});
